import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { createCustomShipment } from '../utils/api';

export default function AddWaypointsModal({ isOpen, onClose, currentDeliveryDate, onSubmit }) {
  const [waypoints, setWaypoints] = useState([]);
  const [currentWaypoint, setCurrentWaypoint] = useState({
    stopName: '',
    address: '',
    phone: '',
    customerName: '',
    latitude: null,
    longitude: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null);

  useEffect(() => {
    // Initialize Google Places Autocomplete
    if (window.google && window.google.maps && window.google.maps.places) {
      const addressInput = document.getElementById('address-input');
      if (addressInput) {
        const autocompleteInstance = new window.google.maps.places.Autocomplete(addressInput);
        setAutocomplete(autocompleteInstance);

        // Add place_changed event listener
        autocompleteInstance.addListener('place_changed', () => {
          const place = autocompleteInstance.getPlace();
          if (place.geometry) {
            setCurrentWaypoint(prev => ({
              ...prev,
              address: place.formatted_address,
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng()
            }));
          }
        });
      }
    }
  }, [isOpen]); // Reinitialize when modal opens

  const handleAddWaypoint = () => {
    if (!currentWaypoint.stopName) {
      toast.error('Stop name and address are required');
      return;
    }

    if (!currentWaypoint.latitude || !currentWaypoint.longitude) {
      toast.error('Please select an address from the suggestions');
      return;
    }

    setWaypoints([...waypoints, { ...currentWaypoint }]);
    setCurrentWaypoint({
      stopName: '',
      address: '',
      phone: '',
      customerName: '',
      latitude: null,
      longitude: null
    });

    // Clear the address input
    const addressInput = document.getElementById('address-input');
    if (addressInput) {
      addressInput.value = '';
    }
  };

  const handleRemoveWaypoint = (index) => {
    setWaypoints(waypoints.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (waypoints.length === 0) {
      toast.error('Please add at least one waypoint');
      return;
    }

    setIsSubmitting(true);
    try {
      const failedWaypoints = [];
      for (const waypoint of waypoints) {
        const result = await createCustomShipment({
          name: waypoint.stopName,
          address: waypoint.address,
          latitude: waypoint.latitude,
          longitude: waypoint.longitude,
          customerName: waypoint.customerName,
          phone: waypoint.phone,
          delivery_date: currentDeliveryDate
        })

        if (!result.success) {
          toast.error('Failed to save waypoints');
          console.error('Error saving waypoints:', result.error);
          failedWaypoints.push(waypoint);
        }
      }
      setWaypoints(failedWaypoints);
      if (failedWaypoints.length === 0) {
        onSubmit();
        onClose();
      }
    } catch (error) {
      toast.error('Failed to save waypoints');
      console.error('Error saving waypoints:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[1000] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative z-[1001]">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Add Waypoints</h3>

        {isSubmitting ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
            <p className="text-gray-600">Saving waypoints...</p>
          </div>
        ) : (
          <>
            {/* Input fields */}
            <div className="space-y-4 mb-6">
              <div>
                <label htmlFor="stop-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Stop Name*
                </label>
                <input
                  type="text"
                  id="stop-name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  value={currentWaypoint.stopName}
                  onChange={(e) => setCurrentWaypoint({ ...currentWaypoint, stopName: e.target.value })}
                  placeholder="Enter stop name"
                />
              </div>

              <div>
                <label htmlFor="address-input" className="block text-sm font-medium text-gray-700 mb-1">
                  Address*
                </label>
                <input
                  type="text"
                  id="address-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Start typing an address..."
                />
              </div>

              <div>
                <label htmlFor="customer-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Name
                </label>
                <input
                  type="text"
                  id="customer-name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  value={currentWaypoint.customerName}
                  onChange={(e) => setCurrentWaypoint({ ...currentWaypoint, customerName: e.target.value })}
                  placeholder="Enter customer name"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  value={currentWaypoint.phone}
                  onChange={(e) => setCurrentWaypoint({ ...currentWaypoint, phone: e.target.value })}
                  placeholder="Enter phone number"
                />
              </div>

              <button
                type="button"
                onClick={handleAddWaypoint}
                className="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Add Waypoint
              </button>
            </div>

            {/* List of added waypoints */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Added Waypoints:</h4>
              {waypoints.length > 0 ? (
                <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md divide-y divide-gray-200">
                  {waypoints.map((waypoint, index) => (
                    <div key={index} className="p-3 flex justify-between items-start">
                      <div className="flex-1 pr-4">
                        <p className="font-medium text-gray-900">{waypoint.stopName}</p>
                        <p className="text-sm text-gray-600">{waypoint.address}</p>
                        {waypoint.customerName && (
                          <p className="text-sm text-gray-500">{waypoint.customerName}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveWaypoint(index)}
                        className="text-red-500 hover:text-red-700"
                        title="Remove"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500 italic border border-gray-300 rounded-md p-4 text-center">
                  No waypoints added yet
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={waypoints.length === 0}
                className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                  waypoints.length === 0 
                    ? 'bg-indigo-400 cursor-not-allowed' 
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                Save Waypoints ({waypoints.length})
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
} 